// src/components/coach/ManageStudentAssignments.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { listAllActiveStudents } from '../../api/users';
import { formatFullName } from '../../utils/name';
import { formatDate } from '../../utils/regionalFormat';
import { showConfirm } from '../../utils/popup';
import { ListLoadingSpinner } from '../layout/ListPageLayout';

const ManageStudentAssignmentsModal = ({ coachId, coachName, isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [assignedStudents, setAssignedStudents] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [assignedSearchQuery, setAssignedSearchQuery] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        if (!isOpen || !coachId) return;
        loadData();
    }, [isOpen, coachId]);

    const loadData = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const assignedRes = await API.get(`/admin/coaches/${coachId}/students`);
            setAssignedStudents(assignedRes.data || []);

            const students = await listAllActiveStudents();
            setAllStudents(Array.isArray(students) ? students : []);
        } catch (error) {
            console.error('Error loading data:', error);
            setAssignedStudents([]);
            setAllStudents([]);
            setLoadError(t('pages.coaches.manageAssignments.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleAssignStudents = async () => {
        if (selectedStudents.length === 0) {
            alert(t('pages.coaches.manageAssignments.selectAtLeastOne'));
            return;
        }

        setSaving(true);
        try {
            await API.post(`/admin/coaches/${coachId}/students/bulk`, selectedStudents);
            alert(t('pages.coaches.manageAssignments.assignSuccess', { count: selectedStudents.length }));
            setSelectedStudents([]);
            setSearchQuery('');
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error assigning students:', error);
            alert(t('pages.coaches.manageAssignments.assignFailed', {
                message: apiErrorMessage(error, t('common.unknownError')),
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveAssignment = async (studentId) => {
        if (!await showConfirm(t('pages.coaches.manageAssignments.removeConfirm'))) return;

        try {
            await API.delete(`/admin/coaches/${coachId}/students/${studentId}`);
            alert(t('pages.coaches.manageAssignments.removeSuccess'));
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error removing assignment:', error);
            alert(t('pages.coaches.manageAssignments.removeFailed'));
        }
    };

    const toggleStudentSelection = (studentId) => {
        setSelectedStudents(prev =>
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    const assignedStudentIds = assignedStudents.map(a => a.studentId);
    const availableStudents = allStudents.filter(s => {
        if (s.active === false) return false;
        const studentId = s.id || s.userId || s.user_id;
        return !assignedStudentIds.includes(studentId);
    });

    const filteredAvailableStudents = availableStudents.filter(s => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        const name = formatFullName(s).toLowerCase();
        const email = (s.email || '').toLowerCase();
        const id = String(s.id || s.userId || s.user_id);
        return name.includes(query) || email.includes(query) || id.includes(query);
    });

    const filteredAssignedStudents = assignedStudents.filter(a => {
        if (!assignedSearchQuery.trim()) return true;
        const query = assignedSearchQuery.toLowerCase();
        const name = (a.studentName || '').toLowerCase();
        const email = (a.studentEmail || '').toLowerCase();
        const id = String(a.studentId ?? '');
        return name.includes(query) || email.includes(query) || id.includes(query);
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

                <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
                    <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-bold text-white">{t('pages.coaches.manageAssignments.title')}</h3>
                                <p className="text-sm text-blue-100 mt-1">{t('pages.coaches.manageAssignments.coachLabel', { name: coachName })}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors"
                                aria-label={t('common.close')}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-start gap-3">
                        <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs text-blue-700 leading-relaxed">
                            {t('pages.coaches.manageAssignments.infoBanner')}
                        </p>
                    </div>

                    {loading ? (
                        <ListLoadingSpinner />
                    ) : loadError ? (
                        <div className="p-6">
                            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                                {loadError}
                            </div>
                            <button
                                type="button"
                                onClick={loadData}
                                className="mt-3 text-sm font-semibold text-blue-700 underline"
                            >
                                {t('common.retry', { defaultValue: 'Retry' })}
                            </button>
                        </div>
                    ) : (
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                        {t('pages.coaches.manageAssignments.assignedStudents', { count: assignedStudents.length })}
                                    </h4>
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            placeholder={t('pages.coaches.manageAssignments.searchAssigned')}
                                            value={assignedSearchQuery}
                                            onChange={(e) => setAssignedSearchQuery(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                                        {assignedStudents.length === 0 ? (
                                            <p className="text-gray-500 text-center py-8">{t('pages.coaches.manageAssignments.noAssigned')}</p>
                                        ) : filteredAssignedStudents.length === 0 ? (
                                            <p className="text-gray-500 text-center py-8">{t('pages.coaches.manageAssignments.noAssignedMatch')}</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {filteredAssignedStudents.map(assignment => (
                                                    <div
                                                        key={assignment.id}
                                                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow"
                                                    >
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900">{assignment.studentName}</p>
                                                            <p className="text-xs text-gray-500">{assignment.studentEmail}</p>
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                {t('common.id')} {assignment.studentId} • {t('pages.coaches.manageAssignments.assignedOn', {
                                                                    date: formatDate(assignment.assignedDate),
                                                                })}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleRemoveAssignment(assignment.studentId)}
                                                            className="ml-3 px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                        >
                                                            {t('common.remove')}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                        {t('pages.coaches.manageAssignments.availableStudents', { count: availableStudents.length })}
                                    </h4>

                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            placeholder={t('pages.coaches.manageAssignments.searchAvailable')}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                                        {filteredAvailableStudents.length === 0 ? (
                                            <p className="text-gray-500 text-center py-8">
                                                {searchQuery
                                                    ? t('pages.coaches.manageAssignments.noStudentsFound')
                                                    : t('pages.coaches.manageAssignments.allAssigned')}
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                {filteredAvailableStudents.map(student => {
                                                    const studentId = student.id || student.userId || student.user_id;
                                                    const isSelected = selectedStudents.includes(studentId);
                                                    return (
                                                        <div
                                                            key={studentId}
                                                            onClick={() => toggleStudentSelection(studentId)}
                                                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${isSelected
                                                                ? 'bg-blue-50 border-blue-300 shadow-sm'
                                                                : 'bg-white border-gray-200 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => { }}
                                                                className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                                            />
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900">{formatFullName(student)}</p>
                                                                <p className="text-xs text-gray-500">{student.email}</p>
                                                                <p className="text-xs text-gray-400">{t('common.id')} {studentId}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {selectedStudents.length > 0 && (
                                        <div className="mt-4">
                                            <button
                                                onClick={handleAssignStudents}
                                                disabled={saving}
                                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {saving
                                                    ? t('pages.coaches.manageAssignments.assigning')
                                                    : t('pages.coaches.manageAssignments.assignButton', { count: selectedStudents.length })}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            {t('common.close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageStudentAssignmentsModal;
